import { AnyAction } from 'redux';

import {
    DEMAND_DETAIL_FEEDBACK_DECLINED,
    DEMAND_DETAIL_FEEDBACK_DETAIL_RECEIVED,
    DEMAND_DETAIL_FEEDBACK_RECEIVED,
    DEMAND_DETAIL_FEEDBACK_SUBMIT,
    DEMAND_DETAIL_FEEDBACK_UPDATE,
} from './demand-detail-feedback-actions';
import { DEMAND_DETAIL_RESET } from '../demand-detail-actions';
import { DemandAccountFeedback } from '../../../../models/demand-account-feedback';
import { DemandAccountParticipant } from '../../../../models/demand-account-participant';
import { DemandAccountStatus } from '../../../../models/demand-account-status';
import { FeedbackFormValues } from '../../models/feedback-form-values';
import { StepData } from '../../../../../../common/components/new-stepper/step-data';

export interface DemandDetailFeedbackReducerState {
    stepData: StepData<FeedbackFormValues>;
    /**
     * The step as the server last handed it over (#2159). `stepData.values`
     * cannot serve: leaving a wizard step parks its input right there.
     */
    baseline?: FeedbackFormValues;
}

export const initialDemandDetailFeedbackReducerState: DemandDetailFeedbackReducerState = {
    stepData: {
        errors: {},
        values: { participants: [] },
        isValid: false,
        isDirty: false,
    },
};

export const demandDetailFeedbackReducer = (
    state: DemandDetailFeedbackReducerState = initialDemandDetailFeedbackReducerState,
    action: AnyAction
): DemandDetailFeedbackReducerState => {
    const { type } = action;

    switch (type) {
        case DEMAND_DETAIL_FEEDBACK_SUBMIT: {
            const data = action['data'] as StepData<FeedbackFormValues>;
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    ...data,
                },
            };
        }

        case DEMAND_DETAIL_FEEDBACK_RECEIVED: {
            const participants = (action['participants'] as DemandAccountParticipant[]) ?? [];
            const selectedParticipants = participants.filter(participant => participant.isSelected);
            const values = { participants: selectedParticipants };

            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    isValid: true,
                    values,
                },
                // The same values, kept apart from the ones the wizard edits.
                baseline: values,
            };
        }

        case DEMAND_DETAIL_FEEDBACK_DETAIL_RECEIVED: {
            const feedback = action['feedback'] as DemandAccountFeedback;
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    isValid: true,
                    values: {
                        ...state.stepData.values,
                        feedbackDetail: feedback,
                    },
                },
            };
        }

        case DEMAND_DETAIL_FEEDBACK_DECLINED: {
            const declinedIds = action['ids'] as number[];
            const declineReason = action['reason'] as string | undefined;
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    values: {
                        ...state.stepData.values,
                        participants: state.stepData.values.participants.map(participant =>
                            declinedIds.includes(participant.id)
                                ? {
                                      ...participant,
                                      status: DemandAccountStatus.Declined,
                                      declineReason: declineReason,
                                  }
                                : participant
                        ),
                    },
                },
            };
        }

        case DEMAND_DETAIL_FEEDBACK_UPDATE: {
            const feedback = action['feedback'] as DemandAccountFeedback;
            const participants = [...state.stepData.values.participants];

            if (participants.length) {
                participants.map(participant => {
                    if (participant.id === feedback.id) {
                        if (feedback.items.length < 1) {
                            participant.status = DemandAccountStatus.Created;
                        } else {
                            participant.status = feedback.status;
                        }
                    }
                    return participant;
                });
            }

            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    values: {
                        ...state.stepData.values,
                        feedbackDetail: feedback,
                        participants,
                    },
                },
            };
        }

        case DEMAND_DETAIL_RESET: {
            return initialDemandDetailFeedbackReducerState;
        }

        default: {
            return state;
        }
    }
};
